"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  cinematicModalSecondaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { StreakDisplay as StreakDisplayType } from "@/lib/streakSystem";
import { motion } from "framer-motion";
import { Calendar, Flame, TrendingUp } from "lucide-react";
import React, { useState } from "react";

interface StreakDisplayProps {
  streakDisplay: StreakDisplayType | null;
  loading: boolean;
  showHoverDetails?: boolean;
  usePortalForModal?: boolean;
  tooltipAlign?: "center" | "end";
}

const StreakDisplay: React.FC<StreakDisplayProps> = ({
  streakDisplay,
  loading,
  showHoverDetails = true,
  usePortalForModal = true,
  tooltipAlign = "end",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  if (loading || !streakDisplay) return null;

  const { currentStreak, longestStreak, title, message } = streakDisplay;
  const tooltipPositionClass =
    tooltipAlign === "center" ? "left-1/2 -translate-x-1/2" : "right-0 left-auto translate-x-0";

  return (
    <div className="relative">
      <motion.button
        type="button"
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsExpanded(true)}
        onMouseEnter={showHoverDetails ? () => setShowDetails(true) : undefined}
        onMouseLeave={showHoverDetails ? () => setShowDetails(false) : undefined}
        className="relative inline-flex h-10 items-center gap-2 rounded-lg border border-white/12 bg-black/20 px-3 text-sm font-semibold text-white/85 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white"
        aria-label={`${currentStreak} day streak. Open details.`}
      >
        <Flame className="h-4 w-4 text-orange-300" aria-hidden="true" />
        <span>{currentStreak}</span>
        <span className="hidden text-white/45 sm:inline">day streak</span>
      </motion.button>

      {showHoverDetails && showDetails && !isExpanded && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          className={`absolute top-full z-50 mt-2 w-64 max-w-[min(16rem,calc(100vw-1.5rem))] rounded-lg border border-white/10 bg-[#121318] px-4 py-3 shadow-[0_18px_50px_rgba(0,0,0,0.45)] ${tooltipPositionClass}`}
        >
          <div className="mb-1 text-sm font-semibold text-white">{title}</div>
          <div className="text-xs leading-relaxed text-white/60">{message}</div>
        </motion.div>
      )}

      <CinematicModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        titleId="streak-dialog-title"
        usePortal={usePortalForModal}
      >
        <div className="flex items-center gap-3 pr-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-400/25 bg-orange-500/10 text-orange-300">
            <Flame className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h3 id="streak-dialog-title" className={cinematicModalTitleClass}>
              {title}
            </h3>
            <p className={cinematicModalSubtitleClass}>Keep the habit moving.</p>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-white/75">{message}</p>

        <div className="mt-4 grid grid-cols-2 border-y border-white/10">
          <div className="py-3 pr-4">
            <div className="mb-1 flex items-center gap-2 text-xs text-white/55">
              <Flame className="h-4 w-4 text-orange-300" aria-hidden="true" />
              Current
            </div>
            <div className="text-xl font-bold text-white">{currentStreak}</div>
            <div className="text-xs text-white/45">{currentStreak === 1 ? "day" : "days"}</div>
          </div>
          <div className="border-l border-white/10 py-3 pl-4">
            <div className="mb-1 flex items-center gap-2 text-xs text-white/55">
              <TrendingUp className="h-4 w-4 text-orange-300" aria-hidden="true" />
              Best
            </div>
            <div className="text-xl font-bold text-white">{longestStreak}</div>
            <div className="text-xs text-white/45">{longestStreak === 1 ? "day" : "days"}</div>
          </div>
        </div>

        {currentStreak > 0 && (
          <div className="mt-4 border-l-2 border-orange-300/45 pl-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
              <Calendar className="h-4 w-4 text-orange-300" aria-hidden="true" />
              Next move
            </div>
            <p className="text-xs leading-relaxed text-white/60">
              {currentStreak < 7
                ? "Come back tomorrow. Tiny repetitions beat heroic cramming."
                : currentStreak < 30
                  ? "This is becoming a habit. Keep the daily conversation alive."
                  : "Your consistency is doing the heavy lifting. Keep going."}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className={`${cinematicModalSecondaryButtonClass} mt-6 w-full`}
        >
          Keep learning
        </button>
      </CinematicModal>
    </div>
  );
};

export default StreakDisplay;
